import { RefObject, useEffect } from "react";

export default function useSearchShortcut(
  inputRef: RefObject<HTMLInputElement | null>,
  key = "j"
) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Only trigger on plain key without modifiers
      if (e.key !== key || e.ctrlKey || e.altKey || e.metaKey) return;

      const active = document.activeElement as HTMLElement | null;
      if (!active) return;

      const tag = active.tagName;
      const isTyping =
        tag === "INPUT" || tag === "TEXTAREA" || active.isContentEditable;
      if (isTyping) return;

      if (inputRef?.current) {
        e.preventDefault();
        try {
          inputRef.current.focus();
          // Select all text when focusing
          inputRef.current.select();
        } catch (err) {
          // ignore selection errors for unsupported input types
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [inputRef, key]);
}

type ShortcutOptions = {
  key: string;
  handler: (e: KeyboardEvent) => void;
  /** When true, ignore events when focus is in an input/textarea/contentEditable. Default: false */
  ignoreIfTyping?: boolean;
  /** When true the hook is active; when false it is disabled. Default: true */
  enabled?: boolean;
  /** Require Ctrl key to be pressed for the shortcut to trigger. Default: false */
  ctrlKey?: boolean;
  /** Require Alt key to be pressed for the shortcut to trigger. Default: false */
  altKey?: boolean;
  /** Require Meta (Cmd) key to be pressed for the shortcut to trigger. Default: false */
  metaKey?: boolean;
  /** Require Shift key to be pressed for the shortcut to trigger. Default: false */
  shiftKey?: boolean;
  /** Match against `e.code` instead of `e.key`. Useful for digit shortcuts since Shift+1 produces "!" in e.key. */
  code?: string;
};

// Generic shortcut hook for other components
export function useShortcut(options: ShortcutOptions) {
  const {
    ignoreIfTyping = false,
    enabled = true,
    ctrlKey = false,
    altKey = false,
    metaKey = false,
    shiftKey = false,
    code,
    key,
    handler,
  } = options || {};

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const pressed = code
        ? e.code === code
        : e.key?.toLowerCase?.() === key.toLowerCase();
      if (!pressed) return;

      if (ignoreIfTyping) {
        const active = document.activeElement as HTMLElement | null;
        if (active) {
          const tag = active.tagName;
          const isTyping =
            tag === "INPUT" || tag === "TEXTAREA" || active.isContentEditable;
          if (isTyping) return;
        }
      }

      // Evaluate modifier requirements.
      // If `ctrlKey` is requested, accept Ctrl OR Meta (Cmd) to make shortcuts work cross-platform.
      if (ctrlKey && !e.ctrlKey && !e.metaKey) return;
      if (altKey && !e.altKey) return;
      // If metaKey is explicitly required, enforce it.
      if (metaKey && !e.metaKey) return;
      if (shiftKey && !e.shiftKey) return;
      if (!shiftKey && e.shiftKey) return;

      handler?.(e);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [key, code, handler, ignoreIfTyping, enabled, ctrlKey, altKey, metaKey, shiftKey]);
}

type ButtonShortcutOptions = Omit<ShortcutOptions, "handler"> & {
  /** Ref to the button element to be "clicked" when the shortcut is triggered */
  buttonRef: RefObject<HTMLButtonElement | HTMLAnchorElement | null>;
};

export function useButtonShortcut(options: ButtonShortcutOptions) {
  const { buttonRef, ...rest } = options;
  useShortcut({
    ...rest,

    handler: (e) => {
      // prevent browser/save dialog
      e.preventDefault();

      if (buttonRef.current) {
        buttonRef.current.click();
      }
    },
  });
}
